/**
 * Wizard-local types. The wizard accumulates state across three steps
 * (firm → program → credentials) and submits at the end via the typed
 * createAccount + validateAccount API. Everything after submit — the
 * live "validating…" chip and failure retry — surfaces on the accounts
 * page, not inside this dialog.
 */

import type {
  AccountPhase,
  AccountRole,
  ChallengeType,
  PlatformType,
} from "@pfh/shared";
import type { FirmRow } from "@/lib/api/firms";

export type { AccountPhase, AccountRole };

export interface WizardState {
  /** Set inside step-firm (auto if the firm supports one platform, or
   *  via an inline mini-picker when the firm supports several). */
  platform: PlatformType | null;
  /** chosen by user in step 1 */
  firm: FirmRow | null;
  /** chosen in step 2 (program/type/size). NULL when the firm has no
   *  variants in firm_challenges. */
  programName: string | null;
  accountSize: number | null;
  steps: number | null;
  challengeType: ChallengeType | null;
  /** firm_challenges.id (cast to string) of the picked variant. Null
   *  for firms with no seeded variants. Lets the server pull the full
   *  rule set straight from the row without re-inferring (size, steps). */
  firmChallengeRef: string | null;
  /** entered in step 3 */
  login: string;
  password: string;
  /** TradeLocker only; required for that platform */
  server: string;
  /** Match-Trader only; the account label/number the user sees on the
   *  account card in the firm dashboard. Required when platform is
   *  match-trader because one email+password fronts many accounts. */
  accountLabel: string;
  /** Optional per-account override for the platform terminal URL. Empty
   *  string = let the worker resolve via firm_platforms / firms fallback
   *  chain. Used when the firm has no URL seeded in D1 or the user wants
   *  to point at a custom branded domain. */
  loginUrl: string;
  /** Defaults; user can change later from the account row */
  role: AccountRole;
  phase: AccountPhase;
  /** account id auto-generated client-side from platform + firm + a short random */
  id: string;
}

export const INITIAL_WIZARD_STATE: WizardState = {
  platform: null,
  firm: null,
  programName: null,
  accountSize: null,
  steps: null,
  challengeType: null,
  firmChallengeRef: null,
  login: "",
  password: "",
  server: "",
  accountLabel: "",
  loginUrl: "",
  role: "long",
  phase: "step1",
  id: "",
};

/** Steps in the wizard, in order. */
export const STEPS = ["firm", "program", "credentials"] as const;
export type StepId = (typeof STEPS)[number];

export interface PlatformOption {
  type: PlatformType;
  label: string;
  description: string;
  /** Path under /public to the platform's logo. */
  logo: string;
  /** Whether the bot has a working driver implementation today. */
  driverReady: boolean;
}

export const PLATFORM_OPTIONS: PlatformOption[] = [
  { type: "mt5", label: "MetaTrader 5", description: "Most prop firms — MT5 web terminal", logo: "/logos/platforms/metatrader-5.svg", driverReady: true },
  { type: "tradelocker", label: "TradeLocker", description: "Blueberry Funded, FundedNext, Blue Guardian", logo: "/logos/platforms/tradelocker.svg", driverReady: true },
  { type: "match-trader", label: "Match Trader", description: "Maven Markets, Funded Engineer", logo: "/logos/platforms/match-trader.svg", driverReady: true },
  { type: "tradovate", label: "Tradovate", description: "Futures prop evaluation platform", logo: "/logos/platforms/tradovate.svg", driverReady: true },
  { type: "ninjatrader", label: "NinjaTrader", description: "Futures prop evaluation platform", logo: "/logos/platforms/ninjatrader.svg", driverReady: true },
  { type: "blackarrow", label: "BlackArrow", description: "Futures prop evaluation platform", logo: "", driverReady: true },
  // MT4's web terminal is a different codebase from MT5; we don't have
  // a driver yet, so expose it as "Coming soon" rather than letting
  // users select it and hit a wall.
  { type: "mt4", label: "MetaTrader 4", description: "Different web terminal from MT5", logo: "/logos/platforms/metatrader-5.svg", driverReady: false },
  { type: "ctrader", label: "cTrader", description: "Coming soon", logo: "/logos/platforms/ctrader.svg", driverReady: false },
  { type: "dx-trade", label: "DX Trade", description: "Coming soon", logo: "/logos/platforms/dxtrade.svg", driverReady: false },
];
